import { useCallback, useContext, useMemo } from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Pill from 'LuxKit/Pill'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import LinePenIcon from 'LuxKit/Icons/line/LinePenIcon'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import Image from 'components/Common/Image'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import TileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'
import CartPanelAccommodationTileLayout from './CartPanelAccommodationTileLayout'
import useCartPanelHotelOfferData from '../hooks/useCartPanelHotelOfferData'
import type { CartPanelAccommodationItem } from '../hooks/useCartPanelItems'
import { useTripPlannerEventBus } from '../contexts/TripPlannerEventBus'

const RoomCard = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(8)} ${rem(16)} ${rem(8)} ${rem(8)};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  width: 100%;
  max-width: ${rem(500)};
  container-type: inline-size;
  container-name: room-card;
`

const RoomCardRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(12)};
`

const RoomCardRightColumn = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  flex-wrap: wrap;
`

const ChangeRoomButtonWrapper = styled.div`
  @container room-card (max-width: ${rem(320)}) {
    flex-basis: 100%;
    display: flex;
    justify-content: flex-start;
  }
`

const RoomImageWrapper = styled.div`
  width: ${rem(68)};
  height: ${rem(68)};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  flex-shrink: 0;
`

const RoomCardContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
`

const RoomName = styled(BodyText)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const PickedForYouPill = styled(Pill)`
  align-self: flex-start;
`

interface Props {
  item: CartPanelAccommodationItem
  onToggleInclude: (item: CartPanelAccommodationItem) => void
  onChangeRoom: (item: CartPanelAccommodationItem) => void
  onDelete: (item: CartPanelAccommodationItem) => void
  isStreaming: boolean
}

function CartPanelActiveHotelTile({
  item,
  onToggleInclude,
  onChangeRoom,
  onDelete,
  isStreaming,
}: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const { offer, pkg, checkIn, checkOut } = useCartPanelHotelOfferData(item)
  const eventBus = useTripPlannerEventBus()

  const handleCardClick = useCallback(() => {
    if (!item.offerId) return
    const searchParams = checkIn || checkOut ? { checkIn, checkOut } : undefined
    eventBus.dispatch({ type: 'open_offer_drawer', offerId: item.offerId, searchParams })
  }, [item.offerId, checkIn, checkOut, eventBus])

  const handleChangeRoom = useCallback(() => {
    onChangeRoom(item)
  }, [onChangeRoom, item])

  const handleDelete = useCallback(() => {
    onDelete(item)
  }, [onDelete, item])

  const isSoldOut = item.pricing.isAvailable === false

  const datesLabel = item.dates && item.duration
    ? intl.formatMessage(
        {
          id: 'VElcmYw8-6lH-Mgm2GV6',
          defaultMessage: '{dateRange} · {nights, plural, one {# night} other {# nights}}',
          description: 'Date range with duration in nights for a hotel stay tile in the trip planner cart panel',
        },
        { dateRange: item.dates, nights: item.duration },
      )
    : item.dates

  const loyaltyCalculationRequest = useMemo(() => {
    if (!offer || !item.pricing.price) return undefined
    return generateLoyaltyPointsCalculatorHotelOptions(offer, {
      pkg,
      price: item.pricing.price,
      memberPrice: item.pricing.memberPrice,
      currentRegionCode,
      nights: item.duration,
    })
  }, [offer, pkg, item.pricing.price, item.pricing.memberPrice, item.duration, currentRegionCode])

  const roomImage = pkg?.roomType.images[0]
  const cancellationPolicyType = pkg?.roomRate.cancellationPolicy.type
  const shouldShowCheckoutDetailsBanner = !item.includeInTotal && !isSoldOut
  const checkoutDetailsBannerAction = item.dates ? intl.formatMessage({
    id: '9Tle--nXWQetSKbmQ8kA',
    defaultMessage: 'Choose package',
    description: 'Action button that opens the hotel drawer so the user can choose a package for checkout',
  }) : intl.formatMessage({
    id: '5NZpwNagm7j-mUPX1BLo',
    defaultMessage: 'Add dates',
    description: 'Action button that opens the hotel drawer so the user can add dates to the hotel',
  })
  const checkoutDetailsBannerDescription = item.dates ? intl.formatMessage({
    id: '9dBPdSSF02mnTeIDmw55',
    defaultMessage: 'Choose a package to include in checkout',
    description: 'Warning banner shown when hotel dates are selected but a package is needed for checkout',
  }) : intl.formatMessage({
    id: 'l37kymVhZxcxOCLV15q6',
    defaultMessage: 'Add dates to include in checkout',
    description: 'Warning banner prompting the user to add dates so the hotel can be included in checkout',
  })

  const checkoutDetailsBanner = shouldShowCheckoutDetailsBanner ? (
    <MessageBanner
      kind="warning"
      density="comfortable"
      primaryActionButtonProps={{
        children: checkoutDetailsBannerAction,
        onClick: handleCardClick,
        disabled: isStreaming || !item.offerId,
        size: 'small',
      }}
    >
      {checkoutDetailsBannerDescription}
    </MessageBanner>
  ) : undefined

  const header = (
    <Group direction="vertical" gap={4}>
      <Heading variant="heading4">{item.name}</Heading>
      {datesLabel && (
        <BodyText variant="medium" colour="neutral-three">
          {datesLabel}
        </BodyText>
      )}
    </Group>
  )

  const roomCard = (item.roomName || item.occupancy) ? (
    <RoomCard>
      <RoomCardRow>
        {roomImage && (
          <RoomImageWrapper>
            <Image image={roomImage} width={80} height={80} fit="center" objectFit="cover" dpr={2} />
          </RoomImageWrapper>
        )}
        <RoomCardRightColumn>
          <RoomCardContent>
            {item.packageAutoSelected && !isSoldOut && (
              <PickedForYouPill kind="tertiary" variant="primary" startIcon={<SolidSparklesIcon />}>
                {intl.formatMessage({
                  id: 'KUUE4XT7BqIq4IZ5Hmc0',
                  defaultMessage: 'Picked for you',
                  description: 'Label indicating the room was auto-selected by the system in the cart panel',
                })}
              </PickedForYouPill>
            )}
            {item.roomName && <RoomName variant="medium" weight="bold">{item.roomName}</RoomName>}
            {item.occupancy && <BodyText variant="medium" colour="neutral-three">{item.occupancy}</BodyText>}
          </RoomCardContent>
          <ChangeRoomButtonWrapper>
            <TextLink size="caption" startIcon={<LinePenIcon />} onClick={handleChangeRoom} disabled={isStreaming}>
              {intl.formatMessage({
                id: 'BrfTYdJonj0A3hETLQR8',
                defaultMessage: 'Change room',
                description: 'Link to change the selected room for a hotel in the cart panel',
              })}
            </TextLink>
          </ChangeRoomButtonWrapper>
        </RoomCardRightColumn>
      </RoomCardRow>
    </RoomCard>
  ) : undefined

  const attention = isSoldOut ? (
    <MessageBanner kind="warning" density="compact" icon={<LineInfoCircleIcon />}>
      {intl.formatMessage({
        id: '33gzZELMLS52IVDsupWp',
        defaultMessage: 'Sold out for your dates',
        description: 'Warning banner shown when a hotel in the cart panel is not available for the selected dates',
      })}
    </MessageBanner>
  ) : (
    <Group direction="vertical" gap={4}>
      {offer && cancellationPolicyType && (
        <TileCancellationPolicy
          cancellationPolicyType={cancellationPolicyType}
          checkInDate={checkIn ?? ''}
          timezoneOffset={offer.property.timezoneOffset || 0}
          offerType={offer.type}
          textSize="medium"
        />
      )}
      {loyaltyCalculationRequest && (
        <LoyaltyPointsEarnWithBreakdownModal
          calculationRequests={[loyaltyCalculationRequest]}
          calculationType="exact"
        />
      )}
    </Group>
  )

  const deleteAction = (
    <TextButton kind="secondary" size="small" shape="round" startIcon={<LineTrashAltIcon />} onClick={handleDelete} disabled={isStreaming}>
      {intl.formatMessage({
        id: 'jDEZjhV3eo-cnxIBHOtR',
        defaultMessage: 'Delete',
        description: 'Button to permanently delete an item from the trip planner cart panel',
      })}
    </TextButton>
  )

  const price = item.pricing.price && !isSoldOut ? (
    <CartReviewPriceStack
      price={item.pricing.price}
      memberPrice={item.pricing.memberPrice}
      value={item.pricing.value ?? 0}
    />
  ) : undefined

  return (
    <CartPanelAccommodationTileLayout
      item={item}
      onToggleInclude={onToggleInclude}
      onCardClick={item.offerId ? handleCardClick : undefined}
      topBanner={checkoutDetailsBanner}
      header={header}
      roomCard={roomCard}
      attention={attention}
      deleteAction={deleteAction}
      price={price}
    />
  )
}

export default CartPanelActiveHotelTile
